import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { FRACTION_ONE } from "@aprao/common/src/math/fraction.js";
import type { WithRelativesSet } from "@aprao/common/src/shapes/common.js";
import { makeAutoObservable } from "mobx";

import { WithRelativesSetDelegate } from "./delegates/WithRelativesSetDelegate.js";

export type NominatorProvidersByKey<K extends string> = Record<
  K,
  () => BigFraction | null
>;

export const withAbsoluteValuePredicate = (delegate: {
  getRelative: (name: "absolute") => unknown;
}) => delegate.getRelative("absolute") != null;

export class RelativesSetFacade<
  K extends string,
  D extends WithRelativesSetDelegate<K>,
> {
  constructor(
    readonly $model: WithRelativesSet<K>,
    readonly $delegate: (target: WithRelativesSet<K>) => D,
  ) {
    makeAutoObservable(this);
  }

  get relativesSet() {
    return this.$delegate(this.$model);
  }
}

export const readOnlyRelativesSetFacadeWithAbsolute = <K extends string>(
  model: WithRelativesSet<K>,
  providers: NominatorProvidersByKey<Exclude<K, "absolute">>,
) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const completedProviders = {
    // absolute is just a relative with factor 1
    absolute: (): BigFraction | null => FRACTION_ONE,
    ...providers,
  } as NominatorProvidersByKey<K>;

  return new RelativesSetFacade(
    model,
    (target) => new WithRelativesSetDelegate(target, completedProviders),
  );
};

export const relativesSetFacadeWithAbsolute = <K extends string>(
  model: WithRelativesSet<K>,
  providers: NominatorProvidersByKey<Exclude<K, "absolute">>,
) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const completedProviders = {
    // absolute is just a relative with factor 1
    absolute: (): BigFraction | null => FRACTION_ONE,
    ...providers,
  } as NominatorProvidersByKey<K>;

  return new RelativesSetFacade<K, WithRelativesSetDelegate<K>>(
    model,
    (target) => new WithRelativesSetDelegate(target, completedProviders),
  );
};
